import React, { useEffect, useState } from "react";
import "./UtilsPage.css";

interface NewsItem {
  title: string;
  link: string;
  pubDate: string;
  thumbnail: string;
  description: string;
}

type Tab = "cripto" | "agenda";

const CRYPTO_FEEDS = [
  "https://livecoins.com.br/feed/",
  "https://www.criptofacil.com/feed/",
  "https://br.beincrypto.com/feed/",
];

function stripHtml(html: string) {
  return (html || "").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

function extractImg(html: string): string {
  const m = (html || "").match(/<img[^>]+src=["']([^"']+)["']/i);
  if (!m) return "";
  let src = m[1];
  if (src.startsWith("//")) src = "https:" + src;
  return src;
}

function timeAgo(dateStr: string) {
  const d = new Date(dateStr).getTime();
  if (!d) return "";
  const h = Math.floor((Date.now() - d) / 3600000);
  if (h < 1) return "há poucos minutos";
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

async function fetchFeed(url: string): Promise<NewsItem[]> {
  const res = await fetch(
    `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`,
  );
  const data = await res.json();
  if (data.status !== "ok") throw new Error("feed");
  return (data.items || []).slice(0, 9).map((it: any) => ({
    title: it.title || "",
    link: it.link || "#",
    pubDate: it.pubDate || "",
    thumbnail: it.thumbnail || it.enclosure?.link || extractImg(it.content) || extractImg(it.description) || "",
    description: stripHtml(it.description || it.content || ""),
  }));
}

async function fetchCrypto(): Promise<NewsItem[]> {
  const settled = await Promise.allSettled(CRYPTO_FEEDS.map((u) => fetchFeed(u)));
  const all: NewsItem[] = [];
  for (const r of settled) if (r.status === "fulfilled") all.push(...r.value);
  if (!all.length) throw new Error("feed");
  const seen = new Set<string>();
  const merged = all.filter((it) => {
    const k = it.title.toLowerCase().replace(/\s+/g, " ").slice(0, 60);
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  merged.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());
  return merged.slice(0, 13);
}

const CALENDAR_SRCDOC = `<!DOCTYPE html><html lang="pt-br"><head><meta charset="utf-8" /><style>html,body{margin:0;padding:0;background:transparent;overflow:hidden}.tradingview-widget-container,.tradingview-widget-container__widget{height:600px}.tradingview-widget-copyright{display:none!important}</style></head><body><div class="tradingview-widget-container"><div class="tradingview-widget-container__widget"></div><script type="text/javascript" src="https://s3.tradingview.com/external-embedding/embed-widget-events.js" async>{"colorTheme":"dark","isTransparent":true,"width":"100%","height":"600","locale":"br","importanceFilter":"0,1","countryFilter":"br,us,eu"}<\/script></div></body></html>`;

const CalendarWidget: React.FC = () => {
  const [ready, setReady] = useState(false);
  return (
    <div className="bc-calendar-frame">
      {!ready ? <div className="bc-calendar-loading"><span className="bc-spin" />Carregando eventos…</div> : null}
      <iframe
        title="Calendário econômico"
        srcDoc={CALENDAR_SRCDOC}
        loading="lazy"
        onLoad={() => window.setTimeout(() => setReady(true), 500)}
      />
    </div>
  );
};

const UtilsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>("cripto");
  const [news, setNews] = useState<Record<string, NewsItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (tab !== "cripto" || news[tab]) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchCrypto()
      .then((items) => { if (!cancelled) setNews((n) => ({ ...n, [tab]: items })); })
      .catch(() => { if (!cancelled) setError("Não foi possível carregar as notícias agora. Tente novamente em instantes."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, news]);

  const items = news[tab] || [];
  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div className="bc-page bc-market">
      <header className="bc-page-head">
        <span className="bc-eyebrow"><i />Mercado cripto</span>
        <h1>Notícias &amp; agenda</h1>
        <p>O que move o mercado cripto agora — pauta pronta pra levar pra live.</p>
      </header>

      <div className="bc-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "cripto"} className={`bc-tab ${tab === "cripto" ? "active" : ""}`} onClick={() => setTab("cripto")}>Notícias</button>
        <button type="button" role="tab" aria-selected={tab === "agenda"} className={`bc-tab ${tab === "agenda" ? "active" : ""}`} onClick={() => setTab("agenda")}>Agenda econômica</button>
      </div>

      {tab === "agenda" ? (
        <section className="bc-panel bc-calendar">
          <div className="bc-panel-head">
            <div className="bc-calendar-title">
              <span className="bc-calendar-ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
              </span>
              <div>
                <h3>Calendário econômico</h3>
                <span className="bc-calendar-hint">Indicadores de alto impacto (BR · EUA · Zona do Euro)</span>
              </div>
            </div>
            <div className="bc-calendar-legend">
              <span><i className="lv lv-3" />Alto</span>
              <span><i className="lv lv-2" />Médio</span>
            </div>
          </div>
          <CalendarWidget />
          <p className="bc-calendar-credit">
            Dados em tempo real por <a href="https://br.tradingview.com/economic-calendar/" target="_blank" rel="noreferrer">TradingView</a>.
          </p>
        </section>
      ) : error ? (
        <div className="bc-empty">{error}</div>
      ) : loading && !items.length ? (
        <div className="bc-news-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="bc-news-card is-sk" key={i}><div className="bc-news-thumb" /><div className="bc-news-body"><span className="sk sk-1" /><span className="sk sk-2" /></div></div>
          ))}
        </div>
      ) : (
        <>
          {featured ? (
            <a className="bc-news-featured" href={featured.link} target="_blank" rel="noreferrer">
              <div className="bc-news-featured-thumb" style={featured.thumbnail ? { backgroundImage: `url("${featured.thumbnail}")` } : undefined}>
                {!featured.thumbnail ? <span>BC</span> : null}
              </div>
              <div className="bc-news-featured-body">
                <span className="bc-news-tag">Cripto · Destaque</span>
                <h2>{featured.title}</h2>
                <p>{featured.description.slice(0, 180)}{featured.description.length > 180 ? "…" : ""}</p>
                <span className="bc-news-meta">{timeAgo(featured.pubDate)}</span>
              </div>
            </a>
          ) : null}

          <div className="bc-news-grid">
            {rest.map((it, i) => (
              <a key={i} className="bc-news-card" href={it.link} target="_blank" rel="noreferrer">
                <div className="bc-news-thumb" style={it.thumbnail ? { backgroundImage: `url("${it.thumbnail}")` } : undefined}>
                  {!it.thumbnail ? <span>BC</span> : null}
                </div>
                <div className="bc-news-body">
                  <h3>{it.title}</h3>
                  <span className="bc-news-meta">{timeAgo(it.pubDate)}</span>
                </div>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default UtilsPage;
